// ui/src/components/calendar/ClosureDialog.tsx
//
// Declare / edit / remove a closure, and flip a public holiday to "open as
// normal". Opened from AdminCalendar either via the top-level "Declare
// closure" button (date/closureId both undefined) or by clicking a day on
// the grid (date always set; closureId set only if that day is inside an
// existing CLOSED closure).
//
// The declare flow is impact-preview-FIRST: fill the form, preview impact,
// THEN confirm. Creating first and previewing after would defeat the entire
// point of the warning (see Task 20 brief, controller addendum #5).
// `closureImpact` is a POST mutation that writes nothing — it must be
// invoked via its trigger function, never treated as a query (addendum #4).
//
// Notification is opt-in, never forced (addendum #6): `notifyChecked`
// defaults to false and the create request always carries the current
// checkbox value, whether ticked or not.
import React, { useEffect, useState } from 'react';
import Modal from '../Modal';
import ConfirmDialog from '../ConfirmDialog';
import {
  useClosureImpactMutation,
  useCreateClosureMutation,
  useUpdateClosureMutation,
  useDeleteClosureMutation,
} from '../../store/api/localApi';
import { Closure, ClosureScope, ClosureStatus } from '../../types';

interface ClassOption {
  id: string;
  name: string;
}

interface ClosureDialogProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: string;
  classes: ClassOption[];
  /** Pre-fills the date range when opened by clicking a specific day. */
  date?: string;
  /** Set when the clicked day falls inside an existing CLOSED closure — puts the dialog in edit mode. */
  closureId?: string;
  existingClosure?: Closure;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

const ClosureDialog: React.FC<ClosureDialogProps> = ({
  isOpen,
  onClose,
  locationId,
  classes,
  date,
  closureId,
  existingClosure,
  onSuccess,
  onError,
}) => {
  const isEditing = Boolean(closureId && existingClosure);

  const [label, setLabel] = useState('');
  const [note, setNote] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [scope, setScope] = useState<ClosureScope>(ClosureScope.LOCATION);
  const [selectedClassIds, setSelectedClassIds] = useState<string[]>([]);
  const [notifyChecked, setNotifyChecked] = useState(false);
  const [impact, setImpact] = useState<{
    sessions: number;
    expectedChildren: number;
    absencesFiled: number;
    affectedFamilies: number;
  } | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [runImpact, { isLoading: impactLoading }] = useClosureImpactMutation();
  const [createClosure, { isLoading: creating }] = useCreateClosureMutation();
  const [updateClosure, { isLoading: updating }] = useUpdateClosureMutation();
  const [deleteClosure, { isLoading: deleting }] = useDeleteClosureMutation();

  // Reset local form state whenever the dialog is (re)opened for a
  // different day/closure — otherwise stale values from a previous open
  // would leak through.
  useEffect(() => {
    if (!isOpen) return;
    if (existingClosure) {
      setLabel(existingClosure.label);
      setNote(existingClosure.note ?? '');
      setStartDate(existingClosure.startDate);
      setEndDate(existingClosure.endDate);
      setScope(existingClosure.scope);
      setSelectedClassIds(existingClosure.classIds ?? []);
    } else {
      setLabel('');
      setNote('');
      setStartDate(date ?? '');
      setEndDate(date ?? '');
      setScope(ClosureScope.LOCATION);
      setSelectedClassIds([]);
    }
    setNotifyChecked(false);
    setImpact(null);
  }, [isOpen, date, existingClosure]);

  const rangeValid = Boolean(startDate) && Boolean(endDate) && endDate >= startDate;
  const scopeValid = scope === ClosureScope.LOCATION || selectedClassIds.length > 0;

  // Why "Confirm" is disabled, in the order the user fills the form. Confirm
  // stays disabled for a NEW closure until Preview impact has run — which is
  // not obvious from a greyed-out button, so surface the reason. `null` = ready.
  const confirmBlockedReason: string | null = !label.trim()
    ? 'Add a label first.'
    : !rangeValid
    ? 'Choose a start and end date (end on or after start).'
    : !scopeValid
    ? 'Pick at least one class for a class-scoped closure.'
    : !isEditing && !impact
    ? 'Preview the impact before confirming.'
    : null;

  async function handlePreviewImpact() {
    if (!rangeValid || !scopeValid) return;
    try {
      const result = await runImpact({
        locationId,
        startDate,
        endDate,
        scope,
        classIds: scope === ClosureScope.CLASS ? selectedClassIds : undefined,
      }).unwrap();
      setImpact(result.impact);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not preview closure impact');
    }
  }

  async function handleConfirm() {
    if (!label.trim() || !rangeValid || !scopeValid) return;
    try {
      if (isEditing && closureId) {
        await updateClosure({
          closureId,
          label,
          note: note || undefined,
          startDate,
          endDate,
          scope,
          classIds: scope === ClosureScope.CLASS ? selectedClassIds : undefined,
        }).unwrap();
        onSuccess('Closure updated');
      } else {
        await createClosure({
          locationId,
          label,
          note: note || undefined,
          startDate,
          endDate,
          scope,
          classIds: scope === ClosureScope.CLASS ? selectedClassIds : undefined,
          status: ClosureStatus.CLOSED,
          notify: notifyChecked,
        }).unwrap();
        onSuccess('Closure declared');
      }
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not save closure');
    }
  }

  async function handleOpenAsNormal() {
    if (!closureId) return;
    try {
      await updateClosure({ closureId, status: ClosureStatus.OPEN_AS_NORMAL }).unwrap();
      onSuccess('Marked as open as normal');
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not update closure');
    }
  }

  async function handleDelete() {
    if (!closureId) return;
    try {
      await deleteClosure({ closureId }).unwrap();
      onSuccess('Closure removed');
      setShowDeleteConfirm(false);
      onClose();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not remove closure');
      setShowDeleteConfirm(false);
    }
  }

  const isHoliday = existingClosure?.source === 'HOLIDAY';
  const busy = impactLoading || creating || updating || deleting;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={isEditing ? 'Edit closure' : 'Declare closure'}
        maxWidth="md"
        testId="closure-dialog"
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="closure-label">
              Label{' '}
              <span className="text-red-500" aria-hidden="true">
                *
              </span>
            </label>
            <input
              id="closure-label"
              type="text"
              required
              aria-required="true"
              data-testid="closure-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Christmas closure"
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label
                className="block text-sm font-medium text-gray-700 mb-1"
                htmlFor="closure-start"
              >
                Start date{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="closure-start"
                type="date"
                required
                aria-required="true"
                data-testid="closure-start-date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="closure-end">
                End date{' '}
                <span className="text-red-500" aria-hidden="true">
                  *
                </span>
              </label>
              <input
                id="closure-end"
                type="date"
                required
                aria-required="true"
                data-testid="closure-end-date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scope</label>
            <div className="flex gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="closure-scope"
                  data-testid="closure-scope-location"
                  checked={scope === ClosureScope.LOCATION}
                  onChange={() => {
                    setScope(ClosureScope.LOCATION);
                    setSelectedClassIds([]);
                  }}
                />
                Whole location
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="radio"
                  name="closure-scope"
                  data-testid="closure-scope-class"
                  checked={scope === ClosureScope.CLASS}
                  onChange={() => setScope(ClosureScope.CLASS)}
                />
                Specific classes
              </label>
            </div>
          </div>

          {scope === ClosureScope.CLASS && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Classes</label>
              <div className="max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-1">
                {classes.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      data-testid={`closure-class-${c.id}`}
                      checked={selectedClassIds.includes(c.id)}
                      onChange={(e) =>
                        setSelectedClassIds((prev) =>
                          e.target.checked ? [...prev, c.id] : prev.filter((id) => id !== c.id)
                        )
                      }
                    />
                    {c.name}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="closure-note">
              Note (optional)
            </label>
            <textarea
              id="closure-note"
              data-testid="closure-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
          </div>

          {!isEditing && (
            <div>
              <button
                type="button"
                data-testid="closure-preview-impact"
                onClick={handlePreviewImpact}
                disabled={!rangeValid || !scopeValid || impactLoading}
                className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                {impactLoading ? 'Checking impact...' : 'Preview impact'}
              </button>
            </div>
          )}

          {impact && (
            <div
              data-testid="closure-impact-summary"
              className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm text-slate-700"
            >
              {impact.sessions} session{impact.sessions === 1 ? '' : 's'} scheduled,{' '}
              {impact.expectedChildren} child{impact.expectedChildren === 1 ? '' : 'ren'} expected,{' '}
              {impact.absencesFiled} absence{impact.absencesFiled === 1 ? '' : 's'} filed
              {impact.affectedFamilies > 0 && (
                <>
                  {' '}
                  ({impact.affectedFamilies} famil{impact.affectedFamilies === 1 ? 'y' : 'ies'}{' '}
                  would be notified)
                </>
              )}
            </div>
          )}

          {!isEditing && impact && (
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                data-testid="closure-notify-checkbox"
                checked={notifyChecked}
                onChange={(e) => setNotifyChecked(e.target.checked)}
              />
              Notify affected families
            </label>
          )}

          {/* Tell the user WHY Confirm is disabled — a greyed-out button with no
              explanation (especially "preview impact first") is the main
              friction here. */}
          {confirmBlockedReason && (
            <p data-testid="closure-confirm-hint" className="text-xs text-gray-500" role="status">
              {confirmBlockedReason}
            </p>
          )}

          <div className="flex items-center justify-between pt-2">
            <div className="flex gap-2">
              {isEditing && closureId && (
                <>
                  {isHoliday && existingClosure?.status === ClosureStatus.CLOSED && (
                    <button
                      type="button"
                      data-testid="closure-open-as-normal"
                      onClick={handleOpenAsNormal}
                      disabled={busy}
                      className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      We&apos;re open that day
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid={`closure-delete-${closureId}`}
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={busy}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-red-700 bg-white hover:bg-red-50 disabled:opacity-50"
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                data-testid="closure-cancel"
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="closure-confirm"
                onClick={handleConfirm}
                disabled={
                  busy || !label.trim() || !rangeValid || !scopeValid || (!isEditing && !impact)
                }
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
              >
                {creating || updating
                  ? 'Saving...'
                  : isEditing
                  ? 'Save changes'
                  : 'Confirm closure'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        title="Remove closure"
        message={`Remove "${
          existingClosure?.label ?? 'this closure'
        }"? Affected days will show as open again.`}
        confirmLabel="Remove"
        variant="danger"
        isLoading={deleting}
      />
    </>
  );
};

export default ClosureDialog;
